import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { PricingSettings } from '../../../shared/constants/pricing';
import type { Section } from '../../../types/tender';
import { EstimatedCostChip } from '../../common/EstimatedCostChip';

interface QuickReviewBannerProps {
  isOwner: boolean;
  onQuickReview: () => void;
  isQuickReviewing: boolean;
  isCompiling: boolean;
  isLimitReached: boolean;

  pricingSettings: PricingSettings | null;
  sections: Section[];
}

export const QuickReviewBanner: React.FC<QuickReviewBannerProps> = ({
  isOwner,
  onQuickReview,
  isQuickReviewing,
  isCompiling,
  isLimitReached,
  pricingSettings,
  sections,
}) => {
  const theme = useTheme();

  if (!isOwner) {
    return null;
  }

  return (
    <Box sx={{ mt: 4, mb: 2 }}>
      <Box
        sx={{
          mb: 2,
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
          <strong>Quick Review</strong> will generate 10 strategic tips for your proposal and
          perform a grammar, clarity, consistency, and tone scan across all sections. You can then
          approve or reject suggestions individually. The review can take up to 30 minutes.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={onQuickReview}
          disabled={isQuickReviewing || isCompiling || isLimitReached}
          startIcon={
            isQuickReviewing ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />
          }
          sx={{
            bgcolor: theme.palette.bidlogiq.brick,
            color: 'white',
            fontWeight: 'bold',
            '&:hover': { bgcolor: theme.palette.bidlogiq.rust },
          }}
        >
          {isQuickReviewing ? 'Quick Review in Progress...' : 'Start Quick Review'}
        </Button>
        {pricingSettings?.estimates?.quick_review_per_section && sections && (
          <Tooltip
            title="Exact billing depends on the length and complexity of the tender documents and provided context."
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
          >
            <EstimatedCostChip
              estimate={`Est. ~$${(
                parseFloat(
                  pricingSettings.estimates.quick_review_per_section.replace(/[^0-9.]/g, '')
                ) * Object.keys(sections).length
              ).toFixed(2)} AUD`}
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};
